import { motion } from 'framer-motion';
import { useEffect,useState } from 'react';
import { FaXTwitter } from 'react-icons/fa6';
import { FiFacebook, FiLink,FiLinkedin } from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import { Link,useParams } from 'react-router';

import { strapiService } from '../../services/strapiService';
import type { Article } from '../../types/article';
import OptimizedImage from '../OptimizedImage';

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) return;

      try {
        setLoading(true);
        const data = await strapiService.getArticleBySlug(slug);
        setArticle(data);
        setError(null);
      } catch (err) {
        setError('Failed to load article. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-surface section">
        <div className="container-custom">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-secondary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-surface section">
        <div className="container-custom">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <p className="text-theme-secondary text-lg mb-4">
                {error || 'Article not found'}
              </p>
              <Link
                to="/wisdom"
                className="btn-primary"
              >
                Back to Wisdom
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      <div className="section">
        <article className="container-custom max-w-4xl">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-8"
          >
            <Link
              to="/wisdom"
              className="inline-flex items-center text-brand-primary dark:text-brand-secondary hover:opacity-80 transition-opacity"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Wisdom
            </Link>
          </motion.div>

          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary dark:text-theme-primary mb-4">
              {article.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-4 text-theme-secondary">
              {article.author && (
                <span className="font-medium">{article.author.name}</span>
              )}
              <span>•</span>
              <time dateTime={article.publishedAt}>
                {new Date(article.publishedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </time>
            </div>

            {/* Social Share Links */}
            <div className="flex items-center gap-3 mt-6">
              <span className="text-theme-secondary text-sm font-medium">Share:</span>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(article.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-panel hover:bg-brand-primary hover:text-white text-theme-secondary transition-colors"
                aria-label="Share on X (Twitter)"
              >
                <FaXTwitter size={18} />
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-panel hover:bg-brand-primary hover:text-white text-theme-secondary transition-colors"
                aria-label="Share on Facebook"
              >
                <FiFacebook size={18} />
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-panel hover:bg-brand-primary hover:text-white text-theme-secondary transition-colors"
                aria-label="Share on LinkedIn"
              >
                <FiLinkedin size={18} />
              </a>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                }}
                className="p-2 rounded-full bg-panel hover:bg-brand-primary hover:text-white text-theme-secondary transition-colors"
                aria-label="Copy link"
              >
                <FiLink size={18} />
              </button>
            </div>
          </motion.header>

          {/* Cover Image */}
          {article.cover && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-12 rounded-2xl overflow-hidden shadow-surface"
            >
              <div className="w-full max-h-[400px]">
                <OptimizedImage
                  src={strapiService.getMediaUrl(article.cover.url)}
                  alt={article.cover.alternativeText || article.title}
                  sizes="(max-width: 1024px) 100vw, 1024px"
                />
              </div>
            </motion.div>
          )}

          {/* Description (intro/summary) */}
          {article.description && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mb-8"
            >
              <p className="text-lg md:text-xl text-theme-secondary leading-relaxed italic border-l-4 border-brand-secondary pl-6">
                {article.description}
              </p>
            </motion.div>
          )}

          {/* Body Content (markdown) */}
          {article.body && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mb-12"
            >
              <div className="bg-card rounded-xl shadow-surface p-8 md:p-12 prose prose-lg dark:prose-invert max-w-none prose-headings:text-primary dark:prose-headings:text-theme-primary prose-p:text-theme-secondary prose-a:text-brand-primary dark:prose-a:text-brand-secondary prose-strong:text-primary dark:prose-strong:text-theme-primary prose-code:bg-panel prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-panel prose-blockquote:border-brand-secondary prose-blockquote:text-theme-secondary">
                <ReactMarkdown>{article.body}</ReactMarkdown>
              </div>
            </motion.div>
          )}

          {/* Footer */}
          <motion.footer
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-12 pt-8 border-t border-theme"
          >
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <Link
                to="/wisdom"
                className="btn-primary"
              >
                ← Back to All Articles
              </Link>

            </div>
          </motion.footer>
        </article>
      </div>
    </div>
  );
};

export default BlogPostPage;
